import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { styled } from "@mui/material";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import {
  createIndividualBookmark,
  deleteIndividualBookmark,
  getIndividualBookmarkByIndividualId,
  populateIndividualBookmarksForUser,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";

const StyledBookmarkButton = styled(LoadingButton)<{ selected: boolean }>(({ selected, theme }) => ({
  backgroundColor: selected ? theme.palette.primary.main : undefined,
  color: selected ? theme.palette.primary.contrastText : undefined,
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.main : undefined,
    color: selected ? theme.palette.primary.contrastText : undefined,
  },
}));

export interface IndividualBookmarkButtonProps {
  className?: string;
  individualId?: Guid;
}

export default function IndividualBookmarkButton(props: Readonly<IndividualBookmarkButtonProps>) {
  const { className, individualId } = props;

  const { enqueueSnackbar } = useSnackbar();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const existingBookmark = useSelector(getIndividualBookmarkByIndividualId(individualId));
  const individualBookmarksLoaded = useSelector(
    (state: RootState) => state.bookmarks.individualBookmarks.entries !== undefined,
  );
  const individualBookmarksLoading = useSelector((state: RootState) => state.bookmarks.individualBookmarks.loading);
  useEffect(() => {
    if (individualBookmarksLoaded || individualBookmarksLoading) return;
    dispatch(populateIndividualBookmarksForUser({ session }));
  }, [dispatch, session, individualId, existingBookmark]);

  async function createBookmark(individualId: Guid) {
    try {
      await dispatch(createIndividualBookmark({ individualId, session })).unwrap();
    } catch (error) {
      console.error("Failed to create individual bookmark:", error);
      return undefined;
    }
  }

  async function deleteBookmark(bookmarkId: Guid) {
    try {
      await dispatch(deleteIndividualBookmark({ bookmarkId, session })).unwrap();
    } catch (error) {
      console.error("Failed to delete individual bookmark:", error);
    }
  }

  async function handleBookmarkClick() {
    try {
      if (existingBookmark) {
        await deleteBookmark(existingBookmark.id);
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!individualId) throw new Error("Bookmark Individual: Individual ID is undefined");
        await createBookmark(individualId);
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  }

  return (
    <StyledBookmarkButton
      className={className}
      selected={existingBookmark !== undefined}
      variant="outlined"
      aria-label="Bookmark"
      loading={individualBookmarksLoading}
      startIcon={<BookmarkBorderIcon />}
      disableElevation
      disabled={!individualId}
      onClick={handleBookmarkClick}
    >
      {existingBookmark ? "Bookmarked" : "Bookmark"}
    </StyledBookmarkButton>
  );
}
